import { Settings, useSettings } from 'core/hooks/useSettings'
import Switch from 'core/components/inputs/Switch'
import { useCallback, useEffect, useState } from 'react'
import Dialog from 'core/components/dialogs/Dialog'
import { useUserProfile } from 'core/hooks/useDataHook'
import Field from 'core/components/form/Field'
import Input from 'core/components/inputs/Input'
import { docCookies } from 'utils/docCookies'
import Button from 'core/components/buttons/Button'
import useNotifications from 'core/hooks/useNotifications'
import { setAuthTokenCookie } from 'utils/AppHelper'

type FeatureFlagName = keyof Settings['featureFlags']

const accounts = [
  {
    auth_token: '182265aa4dc440d48a22dbda76db964f',
    email: 'gabriel+root@everlance.com'
  },
  {
    auth_token: '7d3d8dacc0764891bf13055fc60a0939',
    email: 'clark@everlance.com'
  }
]

/**
 * @description: A widget to control feature flags in the app. Enabled by secret hot-key.
 */
const FeatureFlagController = () => {
  const notification = useNotifications()
  const {
    data: { featureFlags },
    set
  } = useSettings()

  const [show, setShow] = useState(false)
  const toggleShow = useCallback(() => setShow(!show), [show])
  const [authToken, setAuthToken] = useState('')

  const reloadWithNewAuthToken = useCallback(() => {
    setAuthTokenCookie(authToken)
    window.location.reload()
  }, [authToken])

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'm') && (e.metaKey || e.ctrlKey)) {
        toggleShow()
      }
    }
    document.addEventListener('keydown', listener)
    return () => document.removeEventListener('keydown', listener)
  }, [toggleShow])

  const { data: user } = useUserProfile()

  if (
    user?.email &&
    (/@everlance.com$|@test.com$/.test(user.email) ||
      process.env.NODE_ENV === 'development' ||
      process.env.REACT_APP_API_BASE_URL.includes('stage'))
  )
    return (
      <Dialog
        className="top-2 right-2"
        show={show}
        onClose={() => setShow(false)}
        hasBackdrop
      >
        <div className="relative card bg-white w-100">
          <Button
            className="absolute top-1 right-1"
            icon="close"
            onPointerDown={() => setShow(false)}
          />
          <div className="flex justify-between items-center pr-5">
            <span className="font-semibold text-lg">Feature flags </span>{' '}
            <span className="rounded-md text-xs ml-4 bg-dark-1 p-1 font-bold">
              {/mac/i.test(navigator.platform) ? '⌘' : 'CTRL'} + K
            </span>
          </div>
          <p className="text-base text">
            These flags control features in the app.
          </p>
          <div className="w-full mt-8">
            {Object.entries(featureFlags).map(([name]) => {
              // Hide flags that are already approved
              const activeFlags: string[] = []
              return activeFlags.includes(name) &&
                process.env.NODE_ENV === 'production' ? null : (
                <div className="grid grid-cols-2 gap-2 mb-2" key={name}>
                  <div className="text-base ">
                    <pre>
                      <code className="text-dark font-extralight">{name}</code>
                    </pre>
                  </div>
                  <div className="flex justify-end">
                    <Switch
                      onChange={(active) => {
                        set('featureFlags', {
                          ...featureFlags,
                          [name]: active
                        })
                      }}
                      value={featureFlags[name as FeatureFlagName]}
                    />
                  </div>
                </div>
              )
            })}
          </div>
          <br />
          <hr />
          <div className="mt-5">
            <span className="font-semibold text-lg">Prod Accounts </span>
            <div className="flex flex-col gap-3 mt-3">
              {accounts.map((account) => {
                return (
                  <div
                    key={account.auth_token}
                    className="flex items-center justify-between"
                  >
                    <p>{account.email}</p>
                    <Button
                      variant="link"
                      className="text-primary"
                      onClick={() => {
                        setAuthTokenCookie(account.auth_token)
                        window.location.reload()
                      }}
                    >
                      Use
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>
          <br />
          <hr />
          <div className="mt-5">
            <span className="font-semibold text-lg">Auth token </span>{' '}
            <div className="mt-5 flex w-full justify-between items-start">
              <Field helpText='Set a new auth token and click "Set"'>
                <Input
                  placeholder="auth_token"
                  className="input-sm"
                  value={authToken}
                  autoFocus
                  onChange={(e) => {
                    setAuthToken(e.target.value)
                  }}
                  onKeyDown={(e) => {
                    if (authToken && e.code === 'Enter') {
                      reloadWithNewAuthToken()
                    }
                  }}
                />
              </Field>
              <Button
                variant="primary"
                disabled={!authToken}
                onClick={reloadWithNewAuthToken}
              >
                Set
              </Button>
            </div>
            <div className="mt-5 flex w-full justify-between items-start">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const authToken = docCookies.getItem('auth_token')
                    if (authToken) {
                      await navigator.clipboard.writeText(authToken)
                      notification.success('auth_token copied')
                    } else {
                      notification.error('auth_token is empty')
                    }
                  } catch (e) {
                    notification.error(`Couldn't copy auth_token`)
                  }
                }}
              >
                Copy current auth_token
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    )
  return null
}

export default FeatureFlagController
